import { Injectable } from "@angular/core";
import { SessionService } from "./session.service";

export type PracticeStatus = "read_only" | "migrated" | null;

/**
 * PracticeStatusService — surfaces the practice migration state carried on the
 * session JWT (ClioAdmin #85, RC2 QA-enablement auth slice).
 *
 * After login the JWT's `msxInfo` carries two new fields minted by
 * clio-server's /user/msxlogin:
 *   - `msxInfo.practiceStatus`: 'read_only' | 'migrated' | null
 *   - `msxInfo.readOnly`: boolean (true only when practiceStatus === 'read_only')
 *
 * A practice in `read_only` (migration-pending) state can log in and VIEW
 * everything — V2/Apex reports AND the V1 Classic reports toggle — but CANNOT
 * manage the practice (no add/edit/delete of providers, locations, care teams,
 * or practice settings).
 *
 * CRITICAL: this is FAIL OPEN, mirroring `ClassicModeService.isClassicAvailable`.
 * If `practiceStatus`/`msxInfo` is absent (no claim minted yet, or an
 * unrecognised value), we treat the practice as NOT read-only — full
 * management access. Read-only is only asserted when the claim explicitly says
 * so. This keeps the feature testable locally before clio-server mints the
 * claim and never locks an existing user out of management on a missing claim.
 */
@Injectable({ providedIn: "root" })
export class PracticeStatusService {
    constructor(private sessionService: SessionService) {}

    /**
     * Read the `msxInfo` claim off the session user, replicating the
     * `msxinfo` → `msxInfo` lowercase fallback that `getDashboardModules` in
     * pagelayout.component.ts and `ClassicModeService` use.
     */
    private getMsxInfo(): any {
        const attributes = this.sessionService.currentUser?.attributes;
        if (!attributes) {
            return undefined;
        }
        if (attributes.msxinfo !== undefined && attributes.msxInfo === undefined) {
            attributes.msxInfo = attributes.msxinfo;
        }
        return attributes.msxInfo;
    }

    /** The raw practiceStatus claim, or null when absent/unknown. */
    practiceStatus(): PracticeStatus {
        const status = this.getMsxInfo()?.practiceStatus;
        return status === "read_only" || status === "migrated" ? status : null;
    }

    /**
     * Whether the current practice is in the read-only (migration-pending)
     * state. Fail open: returns false when the claim is absent.
     */
    isReadOnly(): boolean {
        const msxInfo = this.getMsxInfo();
        if (!msxInfo) {
            // Fail open — no claim minted yet.
            return false;
        }
        return msxInfo.readOnly === true || msxInfo.practiceStatus === "read_only";
    }
}
